import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '../utils/supabaseClient';

// Types
interface User {
  id: string;
  email?: string;
  [key: string]: any;
}

interface Session {
  user: User | null;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  isSessionReady: boolean;
  setIsAuthenticated: (value: boolean) => void;
  signOut: () => Promise<void>;
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  isAuthenticated: false,
  isSessionReady: false,
  setIsAuthenticated: () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSessionReady, setIsSessionReady] = useState(false); // Initialize new state

  useEffect(() => {
    let mounted = true;

    const handleSessionChange = (session: Session | null) => {
      if (mounted) {
        setUser(session?.user ?? null);
        setIsAuthenticated(!!session?.user);
        setIsSessionReady(true); // Set session ready when session changes
      }
    };

    // Initialize session
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        if (mounted) {
          handleSessionChange(session);
        }
      } catch (error) {
        // Handle error silently or implement proper error handling if needed
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        if (mounted) {
          handleSessionChange(null);
        }
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        if (mounted) {
          handleSessionChange(session);
        }
      }
      
      if (mounted) {
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated,
    isSessionReady,
    setIsAuthenticated,
    signOut: async () => { await supabase.auth.signOut(); }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
} 